import styled from "@emotion/styled";
import { useTheme } from "@kaltura/mediaspace-shared-styled";
import { useMediaQuery, translate } from "@kaltura/mediaspace-shared-utils";
import { Button, Modal, ModalTitle, ModalContent, ModalActions, Link } from "@kaltura/ds-react-components";
import VerticalFilterBar, { VerticalFilterBarProps } from "../vertical-filter-bar/VerticalFilterBar";
import { ButtonAnalyticsProvider, useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";
import { ButtonClickAnalyticsType } from "@kaltura/mediaspace-shared-types";
import { MouseEvent } from "react";

export interface FilterBarProps extends VerticalFilterBarProps {
    isModalOpen?: boolean;
    onModalClose?: () => void;
    inModal?: boolean;
    resultsContainerId?: string;
}

const StyledModalContent = styled(ModalContent)({
    minWidth: 391,
});

const StyledSkipFiltersLink = styled(Link)(({ theme }) => ({
    display: "block",
    position: "absolute",
    left: "-10000px",
    width: "1px",
    height: "1px",
    overflow: "hidden",
    "&:focus-visible": {
        position: "static",
        width: "auto",
        height: "auto",
        overflow: "visible",
        marginBottom: theme.spacing(2),
    },
}));

const baseClassName = "kms-ds-filters-modal";

/**
 * global search filter bar
 */
export function FilterBar(props: FilterBarProps) {
    const { isModalOpen = false, onModalClose, inModal, onClear, resultsContainerId, ...filterbarProps } = props;

    const theme = useTheme();
    const mdScreen = useMediaQuery(theme.breakpoints.up("md"));

    const sendButtonAnalytics = useButtonAnalytics();

    const handleClose = () => {
        sendButtonAnalytics("Close filters", ButtonClickAnalyticsType.TOGGLE, undefined, undefined, "Modal - Filters");

        onModalClose?.();
    };

    const handleSkipFilters = (event: MouseEvent<HTMLAnchorElement>) => {
        event.preventDefault();
        if (resultsContainerId) {
            document.getElementById(resultsContainerId)?.focus();
        }
    };

    return (
        <ButtonAnalyticsProvider location={"Modal - Filters"} applicationFeature={"Search"}>
            {mdScreen && !inModal && (
                <>
                    {resultsContainerId && (
                        <StyledSkipFiltersLink
                            href={`#${resultsContainerId}`}
                            onClick={handleSkipFilters}
                            className="kms-ds-filter-bar-skip-filters-link"
                            size={"small"}
                        >
                            {translate("Skip to search results")}
                        </StyledSkipFiltersLink>
                    )}
                    <VerticalFilterBar {...filterbarProps} onClear={onClear} />
                </>
            )}
            {(!mdScreen || inModal) && (
                <Modal
                    open={isModalOpen}
                    onClose={handleClose}
                    classes={{ paper: baseClassName }}
                    className={`${baseClassName}-outer`}
                >
                    <ModalTitle className={`${baseClassName}-title`}>{translate("Filters")}</ModalTitle>
                    <StyledModalContent className={`${baseClassName}-content`}>
                        <VerticalFilterBar {...filterbarProps} onClear={onClear} />
                    </StyledModalContent>
                    <ModalActions className={`${baseClassName}-actions`}>
                        <Button
                            variant="borderless"
                            color={"secondary"}
                            onClick={handleClose}
                            className={`${baseClassName}-actions-close-button`}
                        >
                            {translate("Close")}
                        </Button>
                    </ModalActions>
                </Modal>
            )}
        </ButtonAnalyticsProvider>
    );
}
export default FilterBar;
